import { useState } from 'react';
import type { ChatTheme, ProfileDraft, ProfileImportResult } from '@kairelo/shared';
import { profile as profileApi, ApiRequestError } from '../lib/api.js';
import { Icon } from './Icon.tsx';
import { Button, Card, Disclosure, Field, Notice, useToast } from './ui.tsx';

/**
 * Drafts a profile from material the owner already published about themselves.
 *
 * Three things this deliberately does *not* do:
 *
 *   - It never saves anything by itself. The result is a draft the owner reads,
 *     edits and then saves with the ordinary Save button.
 *   - It never drafts from a page it could not actually read. Most of the sites
 *     people want to import from — LinkedIn, Facebook, Instagram, most dating
 *     sites — serve a sign-in wall to any request without a session. Drafting
 *     from that wall would produce a confident, complete and entirely invented
 *     biography, so the server refuses and says so instead.
 *   - It never touches conversation content. What is sent for drafting is
 *     public profile material; messages are encrypted in the browser and the
 *     server cannot read them.
 */

interface Props {
  theme: ChatTheme;
  onApply: (draft: ProfileDraft) => void;
  onMediaChanged: () => void;
}

export function ProfileImport({ theme, onApply, onMediaChanged }: Props) {
  const toast = useToast();
  const [url, setUrl] = useState('');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ProfileImportResult | null>(null);
  const [imported, setImported] = useState<string[]>([]);
  const [importing, setImporting] = useState<string | null>(null);

  const run = async () => {
    setError(null);
    setResult(null);
    setImported([]);
    setBusy(true);
    try {
      setResult(
        await profileApi.import({
          ...(url.trim() ? { url: url.trim() } : {}),
          ...(text.trim() ? { text: text.trim() } : {}),
        }),
      );
    } catch (importError) {
      setError(
        importError instanceof ApiRequestError
          ? importError.message
          : 'The draft could not be created.',
      );
    } finally {
      setBusy(false);
    }
  };

  const addImage = async (imageUrl: string) => {
    setImporting(imageUrl);
    try {
      await profileApi.importImage(imageUrl);
      setImported((current) => [...current, imageUrl]);
      onMediaChanged();
      toast('success', 'Photo added to your profile.');
    } catch (imageError) {
      toast(
        'error',
        imageError instanceof ApiRequestError
          ? imageError.message
          : 'That photo could not be added.',
      );
    } finally {
      setImporting(null);
    }
  };

  const applyBranding = async (draft: ProfileDraft) => {
    const { branding } = draft;
    try {
      await profileApi.updateTheme({
        ...theme,
        ...(branding.accentColour ? { accentColour: branding.accentColour } : {}),
        ...(branding.backgroundColour ? { backgroundColour: branding.backgroundColour } : {}),
        ...(branding.surfaceColour ? { surfaceColour: branding.surfaceColour } : {}),
        ...(branding.textColour ? { textColour: branding.textColour } : {}),
        ...(branding.font ? { font: branding.font as ChatTheme['font'] } : {}),
        ...(branding.style ? { style: branding.style as ChatTheme['style'] } : {}),
        ...(branding.corners ? { corners: branding.corners as ChatTheme['corners'] } : {}),
      });
      toast('success', 'Branding applied. Fine-tune it under Chat branding.');
    } catch (brandingError) {
      toast(
        'error',
        brandingError instanceof ApiRequestError
          ? brandingError.message
          : 'The branding could not be applied.',
      );
    }
  };

  const draft = result?.draft ?? null;
  const hasBranding = Boolean(
    draft &&
    (draft.branding.accentColour ||
      draft.branding.backgroundColour ||
      draft.branding.font ||
      draft.branding.style),
  );

  return (
    <Card
      title="Draft from a link"
      description="Paste a page that already describes you — a professional profile, a personal site, a dating profile — and it is turned into a draft you can edit."
      footer={
        <>
          <span className="spacer" />
          <Button
            variant="primary"
            icon="sparkle"
            busy={busy}
            disabled={!url.trim() && !text.trim()}
            onClick={() => void run()}
          >
            Create draft
          </Button>
        </>
      }
    >
      <Field
        label="Link to a page about you"
        htmlFor="import-url"
        hint="Anything publicly readable. Sites that require a sign-in to view a profile cannot be read from here."
      >
        {({ id, describedBy }) => (
          <input
            id={id}
            type="url"
            inputMode="url"
            value={url}
            aria-describedby={describedBy}
            onChange={(event) => setUrl(event.target.value)}
            placeholder="https://…"
            maxLength={2000}
          />
        )}
      </Field>

      <Disclosure summary="Or paste the text yourself">
        <p className="text-sm muted">
          The most reliable route. Open your profile in a browser where you are signed in, select
          the text about you, and paste it here. Nothing else needs to work.
        </p>
        <Field label="Text about you" htmlFor="import-text">
          {({ id }) => (
            <textarea
              id={id}
              value={text}
              onChange={(event) => setText(event.target.value)}
              rows={6}
              maxLength={20000}
              placeholder="Paste the text from your profile."
            />
          )}
        </Field>
      </Disclosure>

      <p className="text-sm muted">
        What you paste here is sent to an external model provider to be turned into a draft.
        Conversation messages are never sent anywhere — they are encrypted in your browser.
      </p>

      {error ? <Notice tone="danger">{error}</Notice> : null}

      {result?.blocked ? (
        <Notice tone="warn" title="That page could not be read">
          {result.reason ?? 'The page did not return anything readable.'} Rather than guess at a
          profile from a page it could not see, nothing was drafted. Paste the text instead — that
          route always works.
        </Notice>
      ) : null}

      {result?.warnings.map((warning) => (
        <Notice tone="warn" key={warning}>
          {warning}
        </Notice>
      ))}

      {draft && draft.insufficient ? (
        <Notice tone="warn" title="Nothing to draft from">
          The source did not contain enough about a person. Try a different page, or paste the text
          about you directly.
        </Notice>
      ) : null}

      {draft && !draft.insufficient ? (
        <div className="draft">
          <h3 className="draft__title">Draft</h3>

          <dl className="draft__fields">
            {draft.displayName ? (
              <>
                <dt>Display name</dt>
                <dd>{draft.displayName}</dd>
              </>
            ) : null}
            {draft.headline ? (
              <>
                <dt>Headline</dt>
                <dd>{draft.headline}</dd>
              </>
            ) : null}
            {draft.bio ? (
              <>
                <dt>About</dt>
                <dd>{draft.bio}</dd>
              </>
            ) : null}
          </dl>

          {draft.notes.length > 0 ? (
            <div className="draft__notes">
              <strong className="text-sm">Not determined from the source</strong>
              <ul className="text-sm muted">
                {draft.notes.map((note) => (
                  <li key={note}>{note}</li>
                ))}
              </ul>
            </div>
          ) : null}

          <div className="draft__actions">
            <Button icon="check" onClick={() => onApply(draft)}>
              Use this draft
            </Button>
            <span className="text-sm muted">
              Fills the fields above. Nothing is saved until you press Save profile.
            </span>
          </div>

          {hasBranding ? (
            <div className="draft__branding">
              <strong className="text-sm">Suggested branding</strong>
              <div className="draft__swatches">
                {(
                  [
                    ['Accent', draft.branding.accentColour],
                    ['Background', draft.branding.backgroundColour],
                    ['Surface', draft.branding.surfaceColour],
                    ['Text', draft.branding.textColour],
                  ] as const
                )
                  .filter(([, value]) => Boolean(value))
                  .map(([label, value]) => (
                    <span className="draft__swatch" key={label}>
                      <span style={{ background: value as string }} aria-hidden="true" />
                      {label} {value}
                    </span>
                  ))}
              </div>
              {draft.branding.rationale ? (
                <p className="text-sm muted">{draft.branding.rationale}</p>
              ) : null}
              <Button size="sm" icon="palette" onClick={() => void applyBranding(draft)}>
                Apply branding
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}

      {result && result.images.length > 0 ? (
        <div className="draft__images">
          <strong className="text-sm">Photos found on that page</strong>
          <p className="text-sm muted">
            Only add photos of yourself that you have the right to use. Each one is downloaded to
            your profile when you add it.
          </p>
          <div className="media-strip">
            {result.images.map((image) => (
              <div className="media-tile" key={image}>
                <img src={image} alt="" loading="lazy" />
                {imported.includes(image) ? (
                  <span className="media-tile__done">
                    <Icon name="check" size={14} /> Added
                  </span>
                ) : (
                  <Button
                    size="sm"
                    icon="plus"
                    busy={importing === image}
                    onClick={() => void addImage(image)}
                  >
                    Add
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </Card>
  );
}
